/**
 * Scrubs sensitive, region-specific, and credential data from workflow nodes
 * before inter-region export, per the "Exported Workflow Requirements for
 * Inter-Region Data Transfer" compliance spec.
 *
 * What is removed / cleared:
 *  - file_data param: download_url, job_id, and all sensitive fields inside
 *    the file object (s3_*, IDs, PII). filename, original_filename, and
 *    file_size are preserved inside a minimal file object so import-time
 *    validation still works (it reads fileValue.file?.filename).
 *  - parser_flag param: value forced to false (disable file parsing on import).
 *  - mcp_servers param: value cleared (contains auth tokens and internal URLs).
 *  - SecretTextInput fields and known credential field IDs: value cleared.
 *  - data.result (node output block) and data.nodeStatusEvents: removed.
 *
 * What is kept:
 *  - filename / original_filename (inside a minimal file object).
 *  - All user-defined text fields (prompts, task descriptions, node names, etc.).
 */

import {
  CustomNode,
  NodeInputType,
  InputValue,
  NodeType,
} from '@/types/node-types';
import {
  FileUploadResponse,
  FileRecord,
} from '@/services/workflow/upload-file';

/** Credential-like field IDs that must always be cleared. */
const CREDENTIAL_FIELD_IDS =
  /^(api_key|client_secret|auth_token|access_token|secret_key|private_key)$/i;

/**
 * Strips sensitive data from a FileUploadResponse stored on a file_data param,
 * while keeping filename, original_filename, and file_size so the import
 * process can present the original file name and size to the user.
 */
function scrubFileDataValue(value: FileUploadResponse): FileUploadResponse {
  const { filename, original_filename, file_size } = (value.file ??
    {}) as Partial<FileRecord>;

  const minimalFile: Partial<FileRecord> | undefined =
    filename || original_filename
      ? ({
          filename: filename ?? '',
          original_filename: original_filename ?? '',
          ...(file_size !== undefined ? { file_size } : {}),
        } as Partial<FileRecord>)
      : undefined;

  return {
    success: value.success,
    message: value.message,
    ...(minimalFile ? { file: minimalFile as FileRecord } : {}),
  };
}

/**
 * Returns a copy of the nodes list with all sensitive data scrubbed.
 * Safe to call on the result of useNodeStore.getState().nodes.
 */
export function scrubNodesForExport(nodes: CustomNode[]): CustomNode[] {
  return nodes
    .filter(
      (n) =>
        n.type !== NodeType.ArtifactNode && n.type !== NodeType.StickyNoteNode,
    )
    .map((node) => {
      const struct = node.data?.struct;
      if (!struct) return node;

      const scrubbedParams = struct.params.map((param) => {
        // 1. File input – keep only filename/original_filename
        if (param.field_id === 'file_data' && param.value) {
          return {
            ...param,
            value: scrubFileDataValue(param.value as FileUploadResponse),
          };
        }

        // 2. Parser flag – disable to prevent auto-parsing in the target region
        if (param.field_id === 'parser_flag') {
          return { ...param, value: false };
        }

        // 3. MCP servers – contains auth tokens and internal service URLs
        if (param.display_modal === NodeInputType.MCP_SERVER_CONFIG) {
          // Clear to an empty servers list rather than undefined to preserve shape
          return {
            ...param,
            value: { mcp_servers: [] } as unknown as InputValue,
          };
        }

        // 4. Secret fields and known credential field IDs
        if (
          param.display_modal === NodeInputType.SECRET_TEXT_INPUT ||
          CREDENTIAL_FIELD_IDS.test(param.field_id)
        ) {
          return { ...param, value: '' as InputValue };
        }

        return param;
      });

      return {
        ...node,
        data: {
          ...node.data,
          // Remove execution output – contains potentially sensitive result text
          result: undefined,
          nodeStatusEvents: [],
          struct: {
            ...struct,
            params: scrubbedParams,
          },
        },
      };
    });
}
